import type { LearnFAQ } from "@/pages/learn/learnShared";
import {
  makeEsArticle,
  esSaintRelated,
  type EsArticleInput,
} from "./learnFactoryEs";

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "November 3, 2026",
  lastReviewed: "November 3, 2026",
  lastUpdated: "November 3, 2026",
};

const faqs: LearnFAQ[] = [
  {
    question: "¿Cómo funciona SAINT® TMS?",
    answer:
      "SAINT® funciona mediante una resonancia magnética funcional (fMRI) que identifica el objetivo de estimulación individualizado de cada paciente, y luego administra estimulación theta burst intermitente — 10 sesiones por día durante 5 días consecutivos con descansos de 50 minutos. La orientación por fMRI y el calendario acelerado la distinguen de la TMS convencional.",
  },
  {
    question: "¿Qué es la estimulación theta burst intermitente (iTBS)?",
    answer:
      "La iTBS es un patrón de pulsos de TMS que administra ráfagas de 3 pulsos a 50 Hz, repetidas a 5 Hz. Administra aproximadamente 1,800 pulsos en una sesión de 10 minutos. El ensayo THREE-D demostró que la iTBS no era inferior a la TMS convencional de 10 Hz cuando se administraba una vez al día. SAINT® usa iTBS en un calendario acelerado de 10 sesiones por día.",
  },
  {
    question: "¿En qué se diferencia SAINT® de la TMS normal?",
    answer:
      "SAINT® usa fMRI para personalizar el objetivo de estimulación, administra 10 sesiones por día durante 5 días (50 en total) y usa patrones de pulsos iTBS. La TMS convencional usa medidas del cuero cabelludo para la orientación, administra una sesión por día durante 4–6 semanas (30–36 en total) y normalmente usa estimulación de 10 Hz. Ningún ensayo las ha comparado directamente.",
  },
  {
    question: "¿Por qué SAINT® usa 10 sesiones por día?",
    answer:
      "El calendario acelerado de 10 sesiones por día con descansos de 50 minutos forma parte del protocolo desarrollado en Stanford. En la investigación, este calendario con iTBS produjo un efecto más rápido y mayor que la estimulación una vez al día. Los descansos permiten que el cerebro responda al patrón de estimulación entre sesiones.",
  },
  {
    question:
      "¿SAINT® estimula una parte distinta del cerebro que la TMS normal?",
    answer:
      "SAINT® estimula la misma región general — la corteza prefrontal dorsolateral izquierda — pero usa fMRI para identificar el punto específico más conectado a los circuitos más profundos reguladores del estado de ánimo en cada paciente. La TMS convencional usa medidas del cuero cabelludo para estimar el objetivo, lo cual es un promedio poblacional y no una ubicación personalizada.",
  },
];

export const esHowDoesSaintWork = makeEsArticle({
  slug: "how-does-saint-tms-work",
  title: "¿Cómo funciona SAINT® TMS? | Neuro Wellness",
  description:
    "Cómo funciona SAINT® TMS: orientación por fMRI, estimulación theta burst intermitente, 10 sesiones por día durante 5 días y en qué se diferencia de la TMS convencional.",
  h1: "¿Cómo funciona SAINT® TMS?",
  breadcrumbLabel: "Cómo funciona SAINT® TMS",
  shortAnswer:
    "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) funciona mediante una resonancia magnética funcional que identifica el objetivo de estimulación cerebral individualizado de cada paciente, y luego administra estimulación theta burst intermitente (iTBS) — 10 sesiones cortas por día durante 5 días consecutivos, con descansos de 50 minutos entre sesiones. Esto difiere de la TMS convencional, que usa medidas del cuero cabelludo y administra una sesión por día durante 4–6 semanas. SAINT® está autorizado por la FDA para adultos con trastorno depresivo mayor que no han mejorado con antidepresivos.",
  keyTakeaways: [
    "SAINT® usa fMRI para identificar el objetivo de estimulación individualizado.",
    "El tratamiento usa estimulación theta burst intermitente (iTBS), un patrón de pulsos específico.",
    "Se administran 10 sesiones por día durante 5 días consecutivos, con descansos de 50 minutos.",
    "El calendario acelerado y la orientación personalizada distinguen a SAINT® de la TMS convencional.",
    "SAINT® está autorizado por la FDA para adultos cuya depresión no ha mejorado con antidepresivos.",
  ],
  sections: [
    {
      heading: "Los tres componentes de SAINT®",
      paragraphs: [
        "SAINT® combina tres elementos que la distinguen de la TMS convencional:",
      ],
      list: [
        "Orientación por fMRI — una resonancia magnética identifica el lugar de estimulación individualizado según la conectividad del propio cerebro del paciente.",
        "Estimulación theta burst intermitente (iTBS) — un patrón de pulsos específico que administra ráfagas de 3 pulsos a 50 Hz, aproximadamente 1,800 pulsos por sesión de 10 minutos.",
        "Calendario acelerado — 10 sesiones por día durante 5 días consecutivos, con descansos de unos 50 minutos entre sesiones, totalizando 50 sesiones en una semana.",
      ],
    },
    {
      heading: "Cómo funciona la orientación por fMRI",
      paragraphs: [
        "Antes del tratamiento, cada paciente se somete a una resonancia magnética estructural y funcional. La fMRI mapea cómo se conectan y comunican las distintas regiones del cerebro. Un algoritmo propietario analiza luego estos datos de conectividad para identificar el punto específico de la corteza prefrontal dorsolateral izquierda más conectado con los circuitos más profundos reguladores del estado de ánimo en el cerebro de esa persona.",
        "Esto difiere de la TMS convencional, que normalmente usa medidas del cuero cabelludo a partir de la ubicación del umbral motor (a menudo la 'regla de 5,5 cm') para estimar dónde colocar la bobina. SAINT® sustituye esa estimación promedio por un objetivo calculado a partir de los datos del propio cerebro del paciente.",
      ],
    },
    {
      heading: "Por qué importa el calendario acelerado",
      paragraphs: [
        "La TMS convencional administra una sesión por día, cinco días a la semana, durante unas 4–6 semanas. SAINT® administra 10 sesiones por día durante 5 días. Los descansos de 50 minutos entre sesiones son una parte deliberada del protocolo — permiten que el cerebro responda al patrón de estimulación.",
        "En la investigación de Stanford, este calendario acelerado con iTBS produjo un efecto más rápido y mayor que la estimación una vez al día. El ensayo doble ciego controlado con simulación de 2022 reportó una remisión del 78,6 % en el grupo activo frente al 13,3 % en el grupo de simulación a las 4 semanas.",
      ],
    },
    {
      heading: "Qué significa para los pacientes",
      paragraphs: [
        "SAINT® funciona mediante una combinación de orientación cerebral personalizada y un calendario de estimulación acelerado. Está autorizado por la FDA para adultos con trastorno depresivo mayor que no han mejorado con antidepresivos. Como requiere una fMRI y el Magnus Neuromodulation System, está disponible en fewer clínicas que la TMS convencional.",
        "Los resultados individuales varían. Los ensayos clínicos fueron pequeños y realizados en un único centro. Una evaluación psiquiátrica determina si SAINT® es apropiada para una persona concreta.",
      ],
      links: [
        {
          to: "/es/saint",
          text: "Más información sobre SAINT® en Neuro Wellness",
        },
        {
          to: "/es/learn/saint-vs-traditional-tms",
          text: "Comparar SAINT® con la TMS tradicional",
        },
        {
          to: "/es/contactus",
          text: "Solicitar una consulta",
          suffix: " o llame al 954-827-9793.",
        },
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): a double-blind randomized controlled trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression. Am J Psychiatry. 2020;177(8):716-726.",
      url: "https://doi.org/10.1176/appi.ajp.2019.19070720",
    },
  ],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);
